/**
 * Pooled audio-transcript annotator — labels live in Postgres, clips stream from S3.
 *
 * Replaces the download-a-dataset AudioTranscriber and keeps what worked there:
 *   • the draft pre-fill, so the job is EDITING rather than transcribing from scratch;
 *   • «Принять» — when the draft is untouched and the clip unjudged, one click accepts it
 *     as gold, with no fake edit needed. That single button is most of the throughput;
 *   • «Шум/неразборчиво» → saves `""`, a real blank target that teaches silence;
 *   • the waveform with detected-speech spans, so you can see where speech sits in a
 *     mostly-silent 30 seconds;
 *   • pinned audio `src` — the URL is re-signed on every fetch, and swapping it
 *     mid-playback makes the browser reload and truncate the clip.
 *
 * What is new is the queue. The old tool showed one downloaded batch (175 clips, all of
 * them already labelled); this one pages 24k straight from the pool, newest recording
 * first, with the same six views and batch mode as the crop annotators.
 *
 * Saves are EXPLICIT here, unlike the crop tools' debounced auto-save: a transcript is
 * typed, and writing on every keystroke would store a dozen half-sentences per clip and
 * make «Принять» meaningless.
 */
import { Select } from '@/components/ui/select';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import {
  Loader2, RefreshCw, ChevronLeft, ChevronRight, Layers, ArrowDownUp, VolumeX, Check,
  AudioLines, Trash2, Undo2, Play,
} from 'lucide-react';
import { useT } from '@/contexts/I18nContext';
import {
  getAudioPoolSamples, getAudioPoolZones, getAudioTrainStatus, audioPoolAnnotate,
  audioPoolDelete, audioPoolRetrain, VIEW_ROWS, viewParams,
  type AudioPoolItem, type Sort, type View,
} from '@/api/audioPool';
import { applyAudioDelta } from './applyAudioDelta';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import ClipPlayer from './ClipPlayer';
import { Tooltip } from '@/components/ui/tooltip';

const PAGE_SIZE = 50;
const viewKey = (v: View) => (v === 'unlabelled' ? 'unlabeled' : v);

export default function PooledAudioAnnotator() {
  const { t } = useT();
  const [zoneId, setZoneId] = useState<number | null>(null);
  const [view, setView] = useState<View>('unlabelled');
  const [batchSize, setBatchSize] = useState(50);
  const [batch, setBatch] = useState(0);
  const [sort, setSort] = useState<Sort>('');
  const { labelled, origin } = viewParams(view);
  const [page, setPage] = useState(1);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const [saved, setSaved] = useState('');
  const [removed, setRemoved] = useState<Set<number>>(new Set());
  const [waveOpen, setWaveOpen] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);

  const { data: zonesResp, error: zonesErr, mutate: retryZones } = useSWR('audio-pool-zones',
    () => getAudioPoolZones().then(r => r.data));
  const zones = zonesResp?.zones ?? [];
  useEffect(() => {
    if (zoneId == null && zones.length) setZoneId(zones[0].zone_id);
  }, [zones, zoneId]);

  const { data: train, mutate: mutateTrain } = useSWR(
    'audio-pool-train-status', () => getAudioTrainStatus().then(r => r.data),
    { revalidateOnFocus: true });

  const key = (zoneId == null && !batch) ? null
    : ['audio-pool', batch ? 'batch' : zoneId, view, batch, sort, page] as const;
  const { data, isLoading, mutate } = useSWR(
    key,
    () => getAudioPoolSamples({ zone_id: zoneId, labelled, origin, batch, sort,
      page, page_size: PAGE_SIZE }).then(r => r.data),
    { revalidateOnFocus: false, keepPreviousData: true },
  );

  const rows = data?.items;
  // Seed each editor from the verdict if there is one, else the draft. `?? ''` is safe
  // ONLY here, where the target is a text input: `corrected_text === ""` and `null` both
  // render as an empty box, and `labelled` (not the string) drives every decision below.
  useEffect(() => {
    if (!rows) return;
    setDrafts(prev => {
      const next = { ...prev };
      for (const it of rows) {
        if (next[it.id] === undefined) next[it.id] = it.corrected_text ?? it.draft_text ?? '';
      }
      return next;
    });
  }, [rows]);

  const items: AudioPoolItem[] = useMemo(
    () => (data?.items ?? []).filter(i => !removed.has(i.id)), [data, removed]);

  const countFor = (v: View) =>
    (v === 'imported' || v === 'collected'
      ? data?.facets.unlabelled_origins?.[v]
      : data?.facets.labelled?.[v]) ?? 0;

  const accounted = useRef<Set<number>>(new Set());
  const viewRef = useRef(view); viewRef.current = view;

  const save = useCallback(async (it: AudioPoolItem, text: string | null) => {
    setSavingId(it.id);
    try {
      await audioPoolAnnotate([{ id: it.id, corrected_text: text }]);
      setDrafts(p => ({ ...p, [it.id]: text ?? '' }));
      if (!accounted.current.has(it.id)) {
        accounted.current.add(it.id);
        void mutate(cur => (cur ? applyAudioDelta(cur, viewRef.current,
                                                  new Map([[it.id, text]])) : cur),
                    { revalidate: false });
      }
      // Patch the row in place so the badge and the button label follow the save without
      // a refetch pulling the list out from under the labeller.
      void mutate(cur => {
        if (!cur) return cur;
        return { ...cur, items: cur.items.map(r => r.id === it.id
          ? { ...r, corrected_text: text, labelled: text !== null, source: text !== null ? 'user' as const : null }
          : r) };
      }, { revalidate: false });
      setSaved(`✓ ${it.audio_record_id}`);
      window.setTimeout(() => setSaved(s => (s.startsWith('✓') ? '' : s)), 1500);
    } catch {
      setSaved('save failed');
    } finally {
      setSavingId(null);
    }
  }, [mutate]);

  const refresh = async () => { accounted.current.clear(); setDrafts({}); await mutate(); };
  const changeFilter = (fn: () => void) => {
    accounted.current.clear(); setDrafts({}); setPage(1); fn();
  };

  const removeClip = async (it: AudioPoolItem) => {
    if (!confirm(t('data.annotate.deletePhotoConfirm'))) return;
    try {
      await audioPoolDelete([it.id]);
      setRemoved(prev => new Set(prev).add(it.id));
    } catch { setSaved('delete failed'); }
  };

  const startRetrain = async () => {
    // Spell out what is about to be trained on: this rents a GPU and moves the deploy tag
    // the central hub serves from, so "N clips, K of them blanks" is the decision itself.
    const lines = [t('data.audio.retrainConfirm')];
    if (train) {
      lines.push('', `${t('data.pool.trainOn')}: ${train.speech} + ${train.blanks} ${t('data.audio.blanks')}`);
      lines.push(`${t('data.audio.heldOut')}: ${train.held_out}`);
      if (train.new_since_last != null) lines.push(`${t('data.pool.trainNew')}: +${train.new_since_last}`);
    }
    if (!confirm(lines.join('\n'))) return;
    setBusy(true);
    try {
      const r = await audioPoolRetrain();
      setSaved(`${t(r.data.already_running ? 'data.pool.retrainAlreadyRunning' : 'data.pool.retrainStarted')} ${r.data.run_id.slice(0, 8)}`);
      void mutateTrain();
      window.open(r.data.url, '_blank', 'noopener');
    } catch (e) {
      setSaved(`retrain failed: ${String((e as { message?: string })?.message ?? e).slice(0, 60)}`);
    } finally { setBusy(false); }
  };

  const toggleWave = (id: number) => setWaveOpen(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const zone = zones.find(z => z.zone_id === zoneId);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / (data.page_size || PAGE_SIZE))) : 1;

  return (
    <div className="space-y-3">
      {/* toolbar */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 rounded-md border bg-background/95 px-3 py-2 backdrop-blur">
        {batch ? (
          <span className="flex items-center gap-2 rounded-md border border-primary/40 bg-primary/10 px-2 py-1 text-sm">
            <Layers className="h-4 w-4" />
            {t('data.pool.batchActive')}: {data?.total ?? 0}
            <button className="text-xs underline" onClick={() => changeFilter(() => setBatch(0))}>
              {t('data.pool.batchExit')}
            </button>
          </span>
        ) : (
          <Select
            className="h-8" wrapperClassName="w-auto"
            value={zoneId ?? ''}
            onChange={e => { const v = Number(e.target.value); changeFilter(() => setZoneId(v)); }}
          >
            {zones.map(z => (
              <option key={z.zone_id} value={z.zone_id}>📍 {z.zone} ({z.total})</option>
            ))}
          </Select>
        )}

        {!batch && (
          <Tooltip content={t('data.pool.batchHint')}><span className="flex items-center gap-1 text-xs">
            <input
              type="number" min={1} max={500} value={batchSize}
              className="w-16 rounded-md border bg-background px-1.5 py-1"
              onChange={e => setBatchSize(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
            />
            <Button variant="outline" size="sm"
                    onClick={() => changeFilter(() => setBatch(batchSize))}>
              <Layers className="mr-1 h-4 w-4" /> {t('data.pool.batchStart')}
            </Button>
          </span></Tooltip>
        )}

        <div className="flex flex-col gap-1">
          {VIEW_ROWS.map((row, i) => (
            <div key={i} className="inline-flex overflow-hidden rounded-md border text-xs">
              {row.map(v => (
                <button
                  key={v}
                  className={`px-2.5 py-1 ${view === v
                    ? 'bg-secondary text-secondary-foreground' : 'text-muted-foreground'}`}
                  onClick={() => changeFilter(() => setView(v))}
                >
                  {t(`data.audio.view.${viewKey(v)}`)} ({countFor(v)})
                </button>
              ))}
            </div>
          ))}
        </div>

        <span className="flex items-center gap-1 rounded-md border px-2 py-1 text-xs">
          <ArrowDownUp className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-muted-foreground">{t('data.pool.sortLabel')}:</span>
          <Select
            wrapperClassName="w-auto"
            className={`h-auto border-0 bg-transparent py-0 pl-0 pr-6 text-xs shadow-none focus-visible:ring-0 ${sort ? 'font-medium text-primary' : ''}`}
            value={sort}
            onChange={e => { const v = e.target.value as Sort; changeFilter(() => setSort(v)); }}
          >
            <option value="">{t('data.audio.sortNewestRec')}</option>
            <option value="oldest">{t('data.audio.sortOldestRec')}</option>
            <option value="annotated_desc">{t('data.pool.sortNewest')}</option>
            <option value="annotated_asc">{t('data.pool.sortOldest')}</option>
          </Select>
        </span>

        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-emerald-600">{saved}</span>
          <span className="text-xs text-muted-foreground">
            {t('data.pool.inFilter')} {data?.total ?? 0}
            {' · '}{t('data.annotate.left')} {zone?.todo ?? 0}
          </span>
          {train && (
            <Tooltip content={t('data.audio.trainHint')}><span className="text-[11px] text-muted-foreground">
              {train.speech} + {train.blanks} {t('data.audio.blanks')}
            </span></Tooltip>
          )}
          <Button variant="outline" size="sm" onClick={refresh} title={t('data.pool.refreshHint')}>
            <RefreshCw className="mr-1 h-4 w-4" /> {t('data.pool.refresh')}
          </Button>
          <Button size="sm" disabled={busy} onClick={startRetrain} title={t('data.audio.retrainHint')}>
            <Play className="mr-1 h-4 w-4" /> {t('data.pool.retrain')}
          </Button>
        </div>
      </div>

      {zonesErr && (
        // 15.09 список зон один раз упал на секундном сбое DNS базы — и вкладка молча стояла на нулях.
        <div className="flex items-center gap-2 rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
          Список зон не загрузился.
          <Button size="sm" variant="outline" onClick={() => void retryZones()}>повторить</Button>
        </div>
      )}
      <p className="text-xs text-muted-foreground">{t('data.audio.hint')}</p>
      {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}

      {/* clip list */}
      <div className="divide-y rounded-md border">
        {items.map(it => {
          const draft = drafts[it.id] ?? '';
          const dirty = it.corrected_text == null
            ? draft !== (it.draft_text ?? '')
            : draft !== it.corrected_text;
          // An unjudged clip is savable even when untouched — that IS «Принять», one
          // click to accept the draft as gold without a fake edit.
          const canSave = !it.labelled || dirty;
          const accepting = !it.labelled && !dirty;
          return (
            <div key={it.id} className="flex flex-col gap-2 p-3 md:flex-row md:items-start">
              <div className="flex w-full shrink-0 flex-col gap-1 md:w-80">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-mono">#{it.audio_record_id}</span>
                  {it.recorded_at && (
                    <span>{new Date(it.recorded_at).toLocaleString('ru-RU',
                      { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                  )}
                  {it.labelled && (
                    <span className="inline-flex items-center gap-0.5 text-green-600 dark:text-green-400">
                      <Check className="size-3" />
                      {it.corrected_text === '' ? t('data.audio.markedNoise') : t('data.audio.marked')}
                    </span>
                  )}
                  <Tooltip content={t('data.annotate.deletePhoto')}><button type="button" className="ml-auto opacity-0 transition hover:text-rose-600 group-hover:opacity-100"
                          aria-label={t('data.annotate.deletePhoto')} onClick={() => removeClip(it)}>
                    <Trash2 className="size-3.5" />
                  </button></Tooltip>
                </div>
                {/* Плеер с волной грузит звук через API — поэтому по кнопке, а не на все 50 строк сразу. */}
                {it.audio_url ? (
                  <button type="button" onClick={() => toggleWave(it.id)}
                          className="inline-flex w-fit items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
                    <AudioLines className="size-3" />
                    {waveOpen.has(it.id) ? t('data.audio.waveHide') : t('data.audio.waveShow')}
                  </button>
                ) : <span className="text-[11px] text-amber-700">{t('data.audio.noAudio')}</span>}
                {waveOpen.has(it.id) && it.audio_url && (
                  <ClipPlayer recId={it.audio_record_id} segments={it.segments ?? []} />
                )}
                {it.prod_text && (
                  <div className="text-[11px] text-muted-foreground">
                    <span className="uppercase tracking-wide">{t('data.audio.prodModel')}:</span> {it.prod_text}
                  </div>
                )}
              </div>
              <div className="flex w-full flex-1 flex-col gap-1">
                <Textarea
                  value={draft}
                  onChange={e => setDrafts(p => ({ ...p, [it.id]: e.target.value }))}
                  onKeyDown={e => {
                    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && canSave) {
                      e.preventDefault(); void save(it, draft);
                    }
                  }}
                  rows={2}
                  placeholder={t('data.audio.placeholder')}
                  className="text-sm"
                />
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="secondary" disabled={!canSave || savingId === it.id}
                          onClick={() => save(it, draft)}>
                    {savingId === it.id ? <Loader2 className="mr-1 size-3 animate-spin" /> : null}
                    {accepting ? t('data.audio.accept') : t('data.audio.save')}
                  </Button>
                  <Button size="sm" variant="ghost" disabled={savingId === it.id}
                          title={t('data.audio.noiseHint')}
                          onClick={() => save(it, '')}>
                    <VolumeX className="mr-1 size-4" /> {t('data.audio.noise')}
                  </Button>
                  {it.labelled && (
                    // Retract: back to the queue. Without it a mis-clicked «Шум» is
                    // permanent and silently poisons training with a blank target.
                    <Button size="sm" variant="ghost" disabled={savingId === it.id}
                            title={t('data.audio.retractHint')}
                            onClick={() => save(it, null)}>
                      <Undo2 className="mr-1 size-4" /> {t('data.audio.retract')}
                    </Button>
                  )}
                  <span className="text-[11px] text-muted-foreground">
                    <kbd className="rounded bg-muted px-1">Ctrl</kbd>+
                    <kbd className="rounded bg-muted px-1">Enter</kbd>
                  </span>
                </div>
              </div>
            </div>
          );
        })}
        {!isLoading && !items.length && (
          <div className="p-6 text-center text-sm text-muted-foreground">{t('data.pool.empty')}</div>
        )}
      </div>

      {data && data.total > (data.page_size || PAGE_SIZE) && (
        <div className="flex items-center justify-center gap-3 py-2">
          <Button variant="outline" size="sm" disabled={page <= 1}
                  onClick={() => { setDrafts({}); setPage(p => Math.max(1, p - 1)); }}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages}
                  onClick={() => { setDrafts({}); setPage(p => p + 1); }}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
